'use client'

import React, { useEffect, useState } from 'react'
import { searchTeams } from '@/apis/team.service'
import {
  Search,
  Loader2,
  Heart,
  X,
} from 'lucide-react'
import Link from 'next/link'
import { useMakerFilter } from '@/hooks/use-maker-filter'
import {
  FilterBarRow,
  FilterMainToggle,
  FilterChipRow,
  JobFilterModal,
} from '@/components/filters/FilterBar'
import { bookmarkTeam, unbookmarkTeam } from '@/apis/bookmark.service'
import { toast } from '@/hooks/use-toast'
import { createBrowserClient } from '@/supabase'

interface Team {
  id: number
  name: string
  bio: string | null
  specialty: string[] | null
  sub_specialty: string[] | null
  prefered: string[] | null
  manager: {
    username: string
  } | null
  team_members: Array<{
    account: {
      username: string
    } | null
  }>
  created_at: string
  active_project_count?: number
}

// 검색어 하이라이트 함수
const highlightText = (text: string, searchTerm: string) => {
  if (!searchTerm.trim()) return text

  const regex = new RegExp(`(${searchTerm})`, 'gi')
  const parts = text.split(regex)

  return parts.map((part, index) =>
    regex.test(part) ? (
      <mark key={index} className="bg-yellow-200 text-gray-900 px-0.5 rounded">
        {part}
      </mark>
    ) : (
      part
    )
  )
}

const SearchTeamsClient = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [teamList, setTeamList] = useState<Team[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredTeamList, setFilteredTeamList] = useState<Team[]>([])
  const { filters, handleFilterChange } = useMakerFilter()
  const [bookmarkedTeams, setBookmarkedTeams] = useState<Set<number>>(new Set())
  const [supabase] = useState(() => (typeof window !== 'undefined' ? createBrowserClient() : null))
  const [isJobFilterModalOpen, setIsJobFilterModalOpen] = useState(false)

  // 북마크 상태 조회
  useEffect(() => {
    const fetchBookmarks = async () => {
      if (!supabase) return

      try {
        const {
          data: { user },
        } = await supabase.auth.getUser()
        if (!user) return

        const { data: profile } = await supabase
          .from('accounts')
          .select('profile_id')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .is('deleted_at', null)
          .maybeSingle()

        if (!profile) return

        const { data: bookmarks } = await supabase
          .from('team_bookmarks' as any)
          .select('team_id')
          .eq('profile_id', (profile as any).profile_id)

        if (bookmarks) {
          setBookmarkedTeams(new Set(bookmarks.map((b: any) => b.team_id)))
        }
      } catch (err) {
        console.error('북마크 조회 실패:', err)
      }
    }

    fetchBookmarks()
  }, [supabase])

  // 팀 프로젝트 개수 조회
  useEffect(() => {
    const fetchProjectCounts = async () => {
      if (!supabase || teamList.length === 0) return

      try {
        const teamIds = teamList.map((t) => t.id)
        const { data: estimates } = await supabase
          .from('estimates')
          .select('proposer_team_id, status')
          .in('proposer_team_id', teamIds)
          .in('status', ['SUBMITTED', 'ACCEPTED'])

        if (estimates) {
          const counts: Record<number, number> = {}
          estimates.forEach((e: any) => {
            const teamId = e.proposer_team_id
            if (teamId) counts[teamId] = (counts[teamId] || 0) + 1
          })

          setTeamList((prev) =>
            prev.map((team) => ({
              ...team,
              active_project_count: counts[team.id] || 0,
            }))
          )
        }
      } catch (err) {
        console.error('프로젝트 개수 조회 실패:', err)
      }
    }

    fetchProjectCounts()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supabase, teamList.length])

  // 검색 및 필터링
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const { data } = await searchTeams({
          searchTerm: searchTerm.trim() || undefined,
          specialty: filters.job.length > 0 ? filters.job : undefined,
        })

        if (data && Array.isArray(data)) {
          setTeamList(data as unknown as Team[])
        } else {
          setTeamList([])
        }
      } catch (err: any) {
        console.error('팀 조회 예외:', err)
        setError(err.message || '팀을 불러오는데 실패했습니다.')
        setTeamList([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [searchTerm, filters.job])

  // 클라이언트 사이드 필터링 (검색어 하이라이트를 위한)
  useEffect(() => {
    let filtered = teamList

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter((team) => {
        return (
          team.name?.toLowerCase().includes(searchLower) ||
          team.bio?.toLowerCase().includes(searchLower) ||
          team.specialty?.some((s) => s.toLowerCase().includes(searchLower)) ||
          team.prefered?.some((p) => p.toLowerCase().includes(searchLower))
        )
      })
    }

    setFilteredTeamList(filtered)
  }, [teamList, searchTerm])

  const clearFilters = () => {
    setSearchTerm('')
    handleFilterChange('job', [])
  }

  const handleJobDeselect = (job: string) => {
    handleFilterChange(
      'job',
      filters.job.filter((j) => j !== job)
    )
  }

  const handleBookmarkToggle = async (teamId: number, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()

    const isBookmarked = bookmarkedTeams.has(teamId)
    const originalSet = new Set(bookmarkedTeams)

    if (isBookmarked) {
      setBookmarkedTeams((prev) => {
        const next = new Set(prev)
        next.delete(teamId)
        return next
      })
    } else {
      setBookmarkedTeams((prev) => new Set(prev).add(teamId))
    }

    try {
      if (isBookmarked) {
        await unbookmarkTeam(teamId)
        toast({ title: '관심 팀에서 제거했습니다.' })
      } else {
        await bookmarkTeam(teamId)
        toast({ title: '관심 팀에 추가했습니다.' })
      }
    } catch (err: any) {
      setBookmarkedTeams(() => originalSet)
      toast({
        variant: 'destructive',
        title: '북마크 실패',
        description: err.message || '북마크 처리에 실패했습니다.',
      })
    }
  }

  const hasActiveFilters = filters.job.length > 0 || searchTerm.trim().length > 0

  const filterChips = [
    ...filters.job.map((job) => ({
      key: `job-${job}`,
      label: job,
      tone: 'blue' as const,
      onClear: () => handleJobDeselect(job),
    })),
    ...(searchTerm.trim()
      ? [
          {
            key: 'search',
            label: `검색: ${searchTerm}`,
            tone: 'gray' as const,
            onClear: () => setSearchTerm(''),
          },
        ]
      : []),
  ]

  return (
    // ✅ 레이아웃이 컨테이너/패딩/상단 여백 담당
    <div className="w-full space-y-8">
      {/* 헤더 */}
      <header className="space-y-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">팀 검색</h1>
        <p className="text-sm text-gray-600 leading-relaxed">함께 일할 팀을 찾아보세요</p>
      </header>

      {/* 검색 및 필터 섹션 */}
      <section className="space-y-3">
        {/* 검색창 */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="팀 이름 · 기술 스택 · 분야로 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all bg-white text-sm"
          />
        </div>

        {/* 필터 바 */}
        <FilterBarRow
          leftContent={
            <JobFilterModal
              value={filters.job}
              onChange={(value) => handleFilterChange('job', value)}
              type="team"
              open={isJobFilterModalOpen}
              onOpenChange={setIsJobFilterModalOpen}
              trigger={
                <FilterMainToggle
                  label="직무"
                  selectedLabel={
                    filters.job.length > 0
                      ? filters.job.length === 1
                        ? filters.job[0]
                        : `${filters.job.length}개 선택`
                      : undefined
                  }
                  expanded={isJobFilterModalOpen}
                  onToggle={() => setIsJobFilterModalOpen((v) => !v)}
                  selected={filters.job.length > 0}
                />
              }
            />
          }
        />

        {/* 선택된 필터 칩 */}
        <FilterChipRow chips={filterChips} />
      </section>

      {/* 결과 통계 */}
      {!isLoading && !error && (
        <div className="text-sm text-gray-600">
          총 <strong className="text-gray-900">{filteredTeamList.length}</strong>개의 팀을 찾았습니다
        </div>
      )}

      {/* 로딩 상태 */}
      {isLoading && (
        <div className="flex items-center justify-center py-20 bg-white border border-gray-200 rounded-xl">
          <div className="text-center space-y-4">
            <Loader2 className="w-8 h-8 text-blue-600 mx-auto animate-spin" />
            <p className="text-sm text-gray-600">팀을 불러오는 중...</p>
          </div>
        </div>
      )}

      {/* 에러 상태 */}
      {error && (
        <div className="text-center py-20 bg-white border border-gray-200 rounded-xl">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">오류가 발생했습니다</h3>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
      )}

      {/* 팀 목록 */}
      {!isLoading && !error && (
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredTeamList.length === 0 ? (
            <div className="col-span-full text-center py-20 bg-white border border-gray-200 rounded-xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">검색 결과가 없습니다</h3>
              <p className="text-sm text-gray-600 mb-6 leading-relaxed">
                {searchTerm || filters.job.length > 0 ? '다른 검색어나 필터로 시도해보세요' : '조건을 변경해보세요'}
              </p>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-sm"
                >
                  필터 초기화
                </button>
              )}
            </div>
          ) : (
            filteredTeamList.map((team) => {
              const isBookmarked = bookmarkedTeams.has(team.id)
              const mainSpecialty = team.specialty?.[0] || ''
              const techStack = team.prefered?.slice(0, 2) || []
              const displayTags = team.specialty?.slice(0, 2) || []
              const remainingTags = (team.specialty?.length || 0) - displayTags.length

              return (
                <Link
                  key={team.id}
                  href={`/freelancer/teams/${team.id}`}
                  className="bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:shadow-sm transition-all p-4 sm:p-5 flex flex-col h-full space-y-3 relative group"
                >
                  {/* 북마크 버튼 */}
                  <button
                    type="button"
                    onClick={(e) => handleBookmarkToggle(team.id, e)}
                    className={`absolute right-4 top-4 z-10 p-2 rounded-lg transition-colors flex-shrink-0 ${
                      isBookmarked ? 'text-blue-600 hover:bg-blue-50' : 'text-gray-400 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                    aria-label={isBookmarked ? '관심 팀 해제' : '관심 팀 추가'}
                  >
                    <Heart className={`w-4 h-4 ${isBookmarked ? 'fill-current' : ''}`} />
                  </button>

                  {/* 팀명 */}
                  <div className="flex items-start gap-4 pr-8">
                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                      {team.name?.[0] || '팀'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-gray-900 truncate">
                        {searchTerm.trim() ? highlightText(team.name, searchTerm) : team.name}
                      </h3>
                    </div>
                  </div>

                  {/* 요약 */}
                  <div className="space-y-1">
                    {mainSpecialty && (
                      <p className="text-sm text-gray-700 font-medium">
                        {mainSpecialty}
                        {techStack.length > 0 && ` | ${techStack.join(', ')}`}
                      </p>
                    )}
                  </div>

                  {/* 소개 */}
                  {team.bio && (
                    <p className="text-sm text-gray-600 leading-relaxed line-clamp-1">
                      {searchTerm.trim() ? highlightText(team.bio, searchTerm) : team.bio}
                    </p>
                  )}

                  {/* 태그 */}
                  {displayTags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {displayTags.map((tag, idx) => (
                        <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-md font-medium">
                          {tag}
                        </span>
                      ))}
                      {remainingTags > 0 && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md">+{remainingTags}</span>
                      )}
                    </div>
                  )}

                  {/* 하단 메타 */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-auto">
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>팀원 {team.team_members?.length || 0}명</span>
                      {team.active_project_count !== undefined && team.active_project_count > 0 && (
                        <span>진행중 {team.active_project_count}</span>
                      )}
                    </div>

                    <span className="text-xs text-blue-600 font-medium">상세 보기</span>
                  </div>
                </Link>
              )
            })
          )}
        </section>
      )}
    </div>
  )
}

export default SearchTeamsClient
